import type {
  ContainerFlow,
  YardDirection,
  YardMovement,
  YardMovesShift,
  YardMoveTransportMode,
} from '@/api/generated.schemas';

/** Segments with this many moves or fewer get no in-segment visit label. */
const MIN_MOVES_FOR_BAR_LABEL = 10;
/** Minimum segment pixel height for a `text-[10px]` label to fit. */
const MIN_LABEL_HEIGHT_PX = 12;
/** Approximate pixel width per character of a `text-[10px]` label. */
const LABEL_CHAR_WIDTH_PX = 6;
/** Visits with fewer total moves in a panel (and not selected) are grouped into "Other visits". */
export const MIN_TOTAL_MOVES_FOR_VISIT_COLOR = 10;
/** Sentinel visit id for the grouped "Other visits" bar segment. */
export const OTHER_VISITS_KEY = '__other__';

export const DEFAULT_LOCALE = 'en-GB';
/** Local hour at which the first shift of a day starts (05/13/21 pattern). */
const FIRST_SHIFT_START_HOUR = 5;

export const FLOW_KEYS = {
  IMPORT: 'general.import',
  EXPORT: 'general.export',
  TRANSSHIPMENT: 'general.transshipment',
} as const satisfies Record<ContainerFlow, string>;

/** Row field per yard direction. */
export const DIRECTION_KEYS = {
  IN: 'in',
  OUT: 'out',
} as const satisfies Record<YardDirection, string>;

export function isYardDirection(
  value: string | undefined
): value is YardDirection {
  return value !== undefined && Object.hasOwn(DIRECTION_KEYS, value);
}

export interface YardMovesFilters {
  containerFlows: Iterable<ContainerFlow>;
  selectedVisitIds: Iterable<string>;
  showSelectedOnly: boolean;
}

/**
 * Keeps movements whose container flow is selected.
 * With `showSelectedOnly` and a non-empty selection, the movement must also
 * relate to at least one selected visit (origin or destination for transshipment).
 * Category (panel) filtering is done by the caller.
 */
export function filterMovements(
  movements: Array<YardMovement>,
  filters: YardMovesFilters
): Array<YardMovement> {
  const flows = new Set(filters.containerFlows);
  const selected = new Set(filters.selectedVisitIds);
  const restrictToSelected = filters.showSelectedOnly && selected.size > 0;

  return movements.filter(
    (movement) =>
      flows.has(movement.container_flow) &&
      (!restrictToSelected ||
        movement.related_ship_visit_ids.some((id) => selected.has(id)))
  );
}

/**
 * Re-keys every movement related to a selected visit onto that visit, so it is
 * drawn in the selected visit's colour (e.g. a transshipment from A displayed
 * under its destination B moves to A when A is selected). Keeps the display
 * visit when it is itself selected; unrelated movements are unchanged.
 */
export function attributeToSelection(
  movements: Array<YardMovement>,
  selected: Set<string>
): Array<YardMovement> {
  if (selected.size === 0) {
    return movements;
  }
  return movements.map((movement) => {
    if (selected.has(movement.display_ship_visit_id)) {
      return movement;
    }
    const selectedId = movement.related_ship_visit_ids.find((id) =>
      selected.has(id)
    );
    return selectedId === undefined
      ? movement
      : { ...movement, display_ship_visit_id: selectedId };
  });
}

interface GroupedMinorVisits {
  movements: Array<YardMovement>;
  /** Visit ids that were remapped to `OTHER_VISITS_KEY`, for the tooltip. */
  groupedVisitIds: Array<string>;
}

/**
 * Remaps `display_ship_visit_id` to `OTHER_VISITS_KEY` for visits whose total
 * moves in this panel are below `MIN_TOTAL_MOVES_FOR_VISIT_COLOR`, unless highlighted.
 */
export function groupMinorVisits(
  panelMovements: Array<YardMovement>,
  highlighted: Iterable<string>
): GroupedMinorVisits {
  const keep = new Set(highlighted);
  const totals = new Map<string, number>();
  for (const movement of panelMovements) {
    const id = movement.display_ship_visit_id;
    totals.set(id, (totals.get(id) ?? 0) + movement.container_count);
  }

  const grouped = new Set<string>();
  for (const [id, total] of totals) {
    if (total < MIN_TOTAL_MOVES_FOR_VISIT_COLOR && !keep.has(id)) {
      grouped.add(id);
    }
  }

  return {
    movements: panelMovements.map((movement) =>
      grouped.has(movement.display_ship_visit_id)
        ? { ...movement, display_ship_visit_id: OTHER_VISITS_KEY }
        : movement
    ),
    groupedVisitIds: Array.from(grouped),
  };
}

export interface YardMovesRow {
  /** Shift start as the raw UTC ISO string; format in tick/tooltip formatters. */
  date: string;
  shiftId: string;
  /** Positive counts per visit id. */
  in: Record<string, number>;
  /** Negative counts per visit id (so OUT bars render below the axis). */
  out: Record<string, number>;
  inTotal: number;
  /** Negative (sum of `out`); use `Math.abs` for display. */
  outTotal: number;
}

export interface YardMovesDetail {
  container_flow: ContainerFlow;
  transport_mode: YardMoveTransportMode;
  container_count: number;
}

export interface YardMovesPanelData {
  rows: Array<YardMovesRow>;
  /** Visit ids present in the panel, by total moves desc, `OTHER_VISITS_KEY` last. */
  visitIds: Array<string>;
  /** Keyed by `detailKey(shiftId, visitId, direction)`. */
  details: Record<string, Array<YardMovesDetail>>;
}

export function detailKey(
  shiftId: string,
  visitId: string,
  direction: YardDirection
): string {
  return `${shiftId}|${visitId}|${direction}`;
}

/**
 * One row per shift (including shifts without moves, so the x-axis is complete).
 * Movements referencing an unknown shift id are ignored.
 */
export function pivotPanelRows(
  panelMovements: Array<YardMovement>,
  shifts: Array<YardMovesShift>
): YardMovesPanelData {
  const rowsByShift = new Map<string, YardMovesRow>(
    shifts.map((shift) => [
      shift.id,
      {
        date: shift.start_time,
        shiftId: shift.id,
        in: {},
        out: {},
        inTotal: 0,
        outTotal: 0,
      },
    ])
  );
  const visitTotals = new Map<string, number>();
  const details: Record<string, Array<YardMovesDetail>> = {};

  for (const movement of panelMovements) {
    const row = rowsByShift.get(movement.shift_id);
    if (row === undefined) {
      continue;
    }
    const visitId = movement.display_ship_visit_id;
    const count = movement.container_count;
    visitTotals.set(visitId, (visitTotals.get(visitId) ?? 0) + count);

    const counts = row[DIRECTION_KEYS[movement.yard_direction]];
    if (movement.yard_direction === 'IN') {
      counts[visitId] = (counts[visitId] ?? 0) + count;
      row.inTotal += count;
    } else {
      counts[visitId] = (counts[visitId] ?? 0) - count;
      row.outTotal -= count;
    }

    const key = detailKey(movement.shift_id, visitId, movement.yard_direction);
    const bucket = (details[key] ??= []);
    const existing = bucket.find(
      (detail) =>
        detail.container_flow === movement.container_flow &&
        detail.transport_mode === movement.transport_mode
    );
    if (existing === undefined) {
      bucket.push({
        container_flow: movement.container_flow,
        transport_mode: movement.transport_mode,
        container_count: count,
      });
    } else {
      existing.container_count += count;
    }
  }

  const visitIds = Array.from(visitTotals.entries())
    .toSorted(([idA, totalA], [idB, totalB]) => {
      if (idA === OTHER_VISITS_KEY) return 1;
      if (idB === OTHER_VISITS_KEY) return -1;
      return totalB - totalA;
    })
    .map(([id]) => id);

  return { rows: Array.from(rowsByShift.values()), visitIds, details };
}

/** e.g. `Tue 14/10 05:00` in the given timezone. */
export function formatShiftLabel(
  iso: string,
  timeZone: string,
  locale = DEFAULT_LOCALE
): string {
  return new Date(iso)
    .toLocaleString(locale, {
      timeZone,
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
    .replaceAll(',', '');
}

/**
 * Day-only x tick: `Tue 14/10` for the first shift of the day (local hour 5),
 * empty string otherwise.
 */
export function formatShiftDayTick(
  iso: string,
  timeZone: string,
  locale = DEFAULT_LOCALE
): string {
  const date = new Date(iso);
  const hour = date.toLocaleString(locale, {
    timeZone,
    hour: '2-digit',
    hourCycle: 'h23',
  });
  if (Number(hour) !== FIRST_SHIFT_START_HOUR) {
    return '';
  }
  return date
    .toLocaleString(locale, {
      timeZone,
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    })
    .replaceAll(',', '');
}

// Weekend shading: reuse `buildWeekendRegions` from utils/scenario.utils.ts with
// `rows` from `pivotPanelRows`. It resolves the weekday in the browser's local
// timezone. Shift starts are 05/13/21 Brussels = 03-04/11-12/19-20 UTC, so the
// UTC and Brussels calendar day always agree.
// ponytail: a browser far west of UTC (e.g. UTC-8) would see the 05:00 Brussels
// shift as the previous day; pass a timezone-aware weekday check if that matters.

export const DEFAULT_TIMEZONE = 'Europe/Brussels';
export interface StackedSegment {
  visitId: string;
  direction: YardDirection;
  /** Signed move count (negative for OUT). */
  value: number;
  /** Stack bounds in data units: IN grows up from 0, OUT grows down. */
  y0: number;
  y1: number;
}

/**
 * Sign-stacks each row's visit counts in `visitIds` order (first visit nearest
 * the axis), skipping zero counts. Replaces one Recharts `<Bar>` per
 * visit×direction: the chart draws these rects itself.
 */
export function stackPanelRows(
  rows: Array<YardMovesRow>,
  visitIds: Array<string>
): Array<Array<StackedSegment>> {
  return rows.map((row) => {
    const segments: Array<StackedSegment> = [];
    for (const direction of ['IN', 'OUT'] as const) {
      let cumulative = 0;
      for (const visitId of visitIds) {
        const value = row[DIRECTION_KEYS[direction]][visitId] ?? 0;
        if (value === 0) continue;
        segments.push({
          visitId,
          direction,
          value,
          y0: cumulative,
          y1: cumulative + value,
        });
        cumulative += value;
      }
    }
    return segments;
  });
}

interface SegmentLabelInput {
  value: number;
  width: number;
  height: number;
  visitId: string;
}

/**
 * Whether a bar segment is big enough (moves and pixels) to carry its visit id,
 * written along its longer side (rotated when taller than wide).
 * `height` may be negative for bars below the axis; "Other visits" is never labelled.
 */
export function shouldLabelSegment({
  value,
  width,
  height,
  visitId,
}: SegmentLabelInput): boolean {
  if (visitId === OTHER_VISITS_KEY) {
    return false;
  }
  const across = Math.min(width, Math.abs(height));
  const along = Math.max(width, Math.abs(height));
  return (
    Math.abs(value) > MIN_MOVES_FOR_BAR_LABEL &&
    across >= MIN_LABEL_HEIGHT_PX &&
    along >= visitId.length * LABEL_CHAR_WIDTH_PX
  );
}

/** Pixel top/height of a stacked segment from its data bounds; `scale` maps data → pixel y. */
export function segmentRect(
  y0: number,
  y1: number,
  scale: (value: number) => number
): { top: number; height: number } {
  const from = scale(y0);
  const to = scale(y1);
  return { top: Math.min(from, to), height: Math.abs(to - from) };
}

/**
 * Centre of a segment's label, rotated -90° when the segment is taller than
 * wide; null when it gets no label (see `shouldLabelSegment`).
 */
export function segmentLabelPlacement({
  left,
  top,
  ...segment
}: SegmentLabelInput & { left: number; top: number }): {
  cx: number;
  cy: number;
  rotate: boolean;
} | null {
  if (!shouldLabelSegment(segment)) {
    return null;
  }
  return {
    cx: left + segment.width / 2,
    cy: top + Math.abs(segment.height) / 2,
    rotate: Math.abs(segment.height) > segment.width,
  };
}

/** Index of the equal-width column under `plotX` (plot-relative), or null outside the plot. */
export function columnAt(
  plotX: number,
  plotWidth: number,
  count: number
): number | null {
  if (count <= 0 || plotX < 0 || plotX >= plotWidth) {
    return null;
  }
  return Math.floor((plotX * count) / plotWidth);
}

/**
 * Tooltip top-left offset from the cursor, flipped to the left / above the
 * cursor when it would overflow the container's right / bottom edge.
 */
export function placeTooltip({
  cursorX,
  cursorY,
  containerWidth,
  containerHeight,
  tooltipWidth,
  tooltipHeight,
  offset,
}: {
  cursorX: number;
  cursorY: number;
  containerWidth: number;
  containerHeight: number;
  tooltipWidth: number;
  tooltipHeight: number;
  offset: number;
}): { left: number; top: number } {
  return {
    left:
      cursorX + offset + tooltipWidth > containerWidth
        ? cursorX - offset - tooltipWidth
        : cursorX + offset,
    top:
      cursorY + offset + tooltipHeight > containerHeight
        ? cursorY - offset - tooltipHeight
        : cursorY + offset,
  };
}
